/**
 * AI Scene Chat Store
 *
 * State management for the AI Scene chatbot feature including:
 * - Chat messages
 * - Modal visibility
 * - Loading states
 * - Conversation persistence
 * - Per-agent usage tracking
 */

import { writable, get } from 'svelte/store';
import LZString from 'lz-string';
import type { ChatMessage } from 'src/services/ai-scene-chat-api';
import type { AgentId, AgentHealthState } from 'src/components/AISceneChatbot/agents/agents.types';
import { AGENTS, DEFAULT_AGENT_ID } from 'src/components/AISceneChatbot/agents/agents.config';
import { postReq } from 'src/services/http';
import { toast } from '@Shelly/shelly-design-system/Toast/toast';
import { save_home } from 'src/helpers/home_helpers';
import { home_data } from 'src/stores/account/user';
import { isSubUnit } from 'src/helpers/user_helpers';

// Constants
const STORAGE_KEY = 'aic';
const MAX_HISTORIES = 20;

// Legacy keys (no longer used, cleaned up on init)
const LEGACY_KEYS = [
	'ai-scene-chat-messages',
	'ai-scene-chat-histories',
	'ai-scene-chat-prefs',
	'ai-scene-chat-button-position',
	'ai-scene-chat-api-mode',
	'ai-scene-chat-prompt-count',
	'ai-scene-chat-prompt-date',
	'ai-scene-chat-last-prompt',
];

/**
 * Per-agent usage statistics
 */
export interface AgentUsageStats {
	dailyPromptCount: number;
	promptCountStartedAt: string | null;
	lastPromptAt: string | null;
}

/**
 * Compressed preferences blob stored in localStorage.
 * Keys: a = active agent, u = per-agent usage map
 */
interface CompressedPrefs {
	a: AgentId;
	u: Record<AgentId, { c: number; s: string | null; l: string | null }>;
}

// Types
export interface ChatHistory {
	id: string;
	title: string;
	messages: ChatMessage[];
	createdAt: number;
	updatedAt: number;
	agentId: AgentId;
}

export interface AIChatState {
	messages: ChatMessage[];
	isLoading: boolean;
	isModalOpen: boolean;
	showOnboardingModal: boolean;
	showPrivacyPolicyModal: boolean;
	privacyPolicyOpenedFromChat: boolean;
	hasAcceptedOnboarding: boolean;
	dismissedWithoutAccepting: boolean;
	unreadCount: number;
	error: string | null;
	histories: ChatHistory[];
	currentHistoryId: string | null;
	activeAgentId: AgentId;
	agentUsage: Record<AgentId, AgentUsageStats>;
	agentHealth: Partial<Record<AgentId, AgentHealthState>>;
	needsNewSession: boolean;
	chatPopoversCompleted: boolean;
	chatPopoverStep: number;
	chatModalOpenTransitionDone: boolean;
}

function getTodayUTC(): string {
	return new Date().toISOString().slice(0, 10);
}

function generateUUID(): string {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

/**
 * Build default usage stats for all agents
 */
function buildDefaultUsage(): Record<AgentId, AgentUsageStats> {
	const usage = {} as Record<AgentId, AgentUsageStats>;
	for (const agent of AGENTS) {
		usage[agent.id] = { dailyPromptCount: 0, promptCountStartedAt: null, lastPromptAt: null };
	}
	return usage;
}

/**
 * Compress usage stats for localStorage
 */
function compressUsage(
	usage: Record<AgentId, AgentUsageStats>,
): Record<AgentId, { c: number; s: string | null; l: string | null }> {
	const compressed = {} as Record<AgentId, { c: number; s: string | null; l: string | null }>;
	for (const agent of AGENTS) {
		const stats = usage[agent.id];
		compressed[agent.id] = {
			c: stats.dailyPromptCount,
			s: stats.promptCountStartedAt,
			l: stats.lastPromptAt,
		};
	}
	return compressed;
}

/**
 * Expand compressed usage stats
 */
function expandUsage(
	compressed: Record<AgentId, { c: number; s: string | null; l: string | null }>,
): Record<AgentId, AgentUsageStats> {
	const usage = {} as Record<AgentId, AgentUsageStats>;
	const today = getTodayUTC();

	for (const agent of AGENTS) {
		const data = compressed[agent.id];
		if (data) {
			const isCurrentDay = data.s === today;
			usage[agent.id] = {
				dailyPromptCount: isCurrentDay ? data.c : 0,
				promptCountStartedAt: isCurrentDay ? data.s : null,
				lastPromptAt: data.l,
			};
		} else {
			usage[agent.id] = { dailyPromptCount: 0, promptCountStartedAt: null, lastPromptAt: null };
		}
	}
	return usage;
}

/**
 * Unified storage blob stored under the 'aic' key
 * m = messages, h = histories, p = prefs, o = onboarding accepted
 */
interface AicBlob {
	m: ChatMessage[];
	h: ChatHistory[];
	p: CompressedPrefs;
	o?: boolean;
}

function getDefaultAicBlob(): AicBlob {
	const defaultUsage = compressUsage(buildDefaultUsage());
	return {
		m: [],
		h: [],
		p: { a: DEFAULT_AGENT_ID, u: defaultUsage },
	};
}

function loadAic(): AicBlob {
	const defaults = getDefaultAicBlob();

	try {
		// Clean up legacy keys on every load
		LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));

		const stored = localStorage.getItem(STORAGE_KEY);
		if (!stored) return defaults;

		const decompressed = LZString.decompressFromUTF16(stored);
		if (decompressed) {
			const parsed = JSON.parse(decompressed);
			return {
				m: Array.isArray(parsed.m) ? parsed.m : defaults.m,
				h: Array.isArray(parsed.h) ? parsed.h : defaults.h,
				p: parsed.p?.a && parsed.p?.u ? parsed.p : defaults.p,
				o: parsed.o === true,
			};
		}

		const parsed = JSON.parse(stored);
		return {
			m: Array.isArray(parsed.m) ? parsed.m : defaults.m,
			h: Array.isArray(parsed.h) ? parsed.h : defaults.h,
			p: parsed.p?.a && parsed.p?.u ? parsed.p : defaults.p,
			o: parsed.o === true,
		};
	} catch (e) {
		console.warn('Failed to load aic from localStorage:', e);
		return defaults;
	}
}

function saveAic(blob: AicBlob) {
	try {
		const compressed = LZString.compressToUTF16(JSON.stringify(blob));
		localStorage.setItem(STORAGE_KEY, compressed);
	} catch (e) {
		console.warn('Failed to save aic to localStorage:', e);
	}
}

function saveMessages(messages: ChatMessage[]) {
	// Filter out health check error messages - they shouldn't be persisted
	const persistableMessages = messages.filter((m) => !m.isHealthCheckError);
	const blob = loadAic();
	saveAic({ ...blob, m: persistableMessages });
}

function saveHistories(histories: ChatHistory[]) {
	const blob = loadAic();
	saveAic({ ...blob, h: histories });
}

function savePrefs(prefs: CompressedPrefs) {
	const blob = loadAic();
	saveAic({ ...blob, p: prefs });
}

function generateTitle(messages: ChatMessage[]): string {
	const firstUserMsg = messages.find((m) => m.role === 'user');
	if (!firstUserMsg) return 'New Conversation';

	const title = firstUserMsg.content.slice(0, 50);
	return title.length < firstUserMsg.content.length ? `${title}...` : title;
}

function saveHasAcceptedOnboarding(accepted: boolean) {
	const blob = loadAic();
	saveAic({ ...blob, o: accepted });
}

const AI_CHAT_ONBOARDING_ACCEPTED = 'aiChatOnboardingAccepted';
const AI_CHAT_POPOVERS_KEY = 'aiChatPopoversCompleted';

function hasAcceptedOnboardingFromHomeData(): boolean {
	const hd = get(home_data);
	const val =
		hd && typeof hd === 'object'
			? (hd as Record<string, unknown>)[AI_CHAT_ONBOARDING_ACCEPTED]
			: undefined;
	// Support both { accepted: true/false } and legacy boolean true
	if (val === true) return true;
	if (val && typeof val === 'object' && (val as Record<string, unknown>).accepted === true)
		return true;
	return false;
}

function getChatPopoversStateFromHomeData(): { completed: boolean; step: number } {
	const hd = get(home_data);
	const val =
		hd && typeof hd === 'object' ? (hd as Record<string, unknown>)[AI_CHAT_POPOVERS_KEY] : undefined;
	if (val && typeof val === 'object') {
		const obj = val as Record<string, unknown>;
		if (obj.completed === true) return { completed: true, step: 0 };
		return { completed: false, step: typeof obj.step === 'number' ? obj.step : 0 };
	}
	return { completed: false, step: 0 };
}

function getInitialState(): AIChatState {
	const blob = loadAic();
	const agentUsage = expandUsage(blob.p.u);
	const hasAcceptedOnboarding = blob.o === true || hasAcceptedOnboardingFromHomeData();
	const popoversState = getChatPopoversStateFromHomeData();

	return {
		messages: blob.m,
		isLoading: false,
		isModalOpen: false,
		showOnboardingModal: false,
		showPrivacyPolicyModal: false,
		privacyPolicyOpenedFromChat: false,
		hasAcceptedOnboarding,
		dismissedWithoutAccepting: false,
		unreadCount: 0,
		error: null,
		histories: blob.h,
		currentHistoryId: null,
		activeAgentId: blob.p.a,
		agentUsage,
		agentHealth: {},
		needsNewSession: false,
		chatPopoversCompleted: popoversState.completed,
		chatPopoverStep: popoversState.step,
		chatModalOpenTransitionDone: false,
	};
}

function createAISceneChatStore() {
	const { subscribe, set, update } = writable<AIChatState>(getInitialState());

	const aiSceneChatStore = {
		subscribe,

		addMessage(message: ChatMessage) {
			update((state) => {
				const stamped = { ...message, agentId: message.agentId ?? state.activeAgentId };
				const newMessages = [...state.messages, stamped];
				saveMessages(newMessages);

				const newUnreadCount =
					!state.isModalOpen && message.role === 'assistant'
						? state.unreadCount + 1
						: state.unreadCount;

				return {
					...state,
					messages: newMessages,
					unreadCount: newUnreadCount,
				};
			});
		},

		setLoading(isLoading: boolean) {
			update((state) => ({ ...state, isLoading }));
		},

		setError(error: string | null) {
			update((state) => ({ ...state, error }));
		},

		setAgentHealth(agentId: AgentId, health: AgentHealthState) {
			update((state) => ({
				...state,
				agentHealth: { ...state.agentHealth, [agentId]: health },
			}));
		},

		openModal() {
			update((state) => {
				const accepted =
					state.hasAcceptedOnboarding ||
					(!state.dismissedWithoutAccepting && hasAcceptedOnboardingFromHomeData());
				if (accepted) {
					return {
						...state,
						isModalOpen: true,
						unreadCount: 0,
						chatModalOpenTransitionDone: true,
					};
				}
				return { ...state, showOnboardingModal: true, unreadCount: 0 };
			});
		},

		acceptOnboarding() {
			saveHasAcceptedOnboarding(true);
			save_home(AI_CHAT_ONBOARDING_ACCEPTED, { accepted: true });
			update((state) => ({
				...state,
				hasAcceptedOnboarding: true,
				dismissedWithoutAccepting: false,
				showOnboardingModal: false,
				isModalOpen: true,
				unreadCount: 0,
				// introend often doesn't fire when opening from {#if}; set true here so popover can show (on:introend still sets it when it does fire)
				chatModalOpenTransitionDone: true,
			}));
		},

		dismissOnboarding() {
			update((state) => ({
				...state,
				showOnboardingModal: false,
				dismissedWithoutAccepting: true,
			}));
		},

		openPrivacyPolicy(fromChat = false) {
			update((state) => ({
				...state,
				isModalOpen: false,
				showOnboardingModal: false,
				showPrivacyPolicyModal: true,
				privacyPolicyOpenedFromChat: fromChat,
			}));
		},

		closePrivacyPolicy() {
			update((state) => ({
				...state,
				showPrivacyPolicyModal: false,
				privacyPolicyOpenedFromChat: false,
				isModalOpen: state.privacyPolicyOpenedFromChat,
				showOnboardingModal: !state.privacyPolicyOpenedFromChat && !state.hasAcceptedOnboarding,
			}));
		},

		resetOnboarding() {
			saveHasAcceptedOnboarding(false);
			save_home(AI_CHAT_ONBOARDING_ACCEPTED, { accepted: false });
			update((state) => ({
				...state,
				hasAcceptedOnboarding: false,
				dismissedWithoutAccepting: false,
				showOnboardingModal: false,
				showPrivacyPolicyModal: false,
				isModalOpen: false,
			}));

			toast.open({
				title: 'AI Chat Onboarding Reset',
				type: 'success',
			});
		},

		closeModal() {
			update((state) => ({
				...state,
				isModalOpen: false,
				chatModalOpenTransitionDone: false,
			}));
		},

		setChatModalOpenTransitionDone(done: boolean) {
			update((state) => ({ ...state, chatModalOpenTransitionDone: done }));
		},

		toggleModal() {
			const state = get(aiSceneChatStore);
			if (state.isModalOpen) {
				aiSceneChatStore.closeModal();
			} else {
				aiSceneChatStore.openModal();
			}
		},

		clearHistory() {
			update((state) => ({
				...state,
				messages: [],
				error: null,
				currentHistoryId: null,
			}));

			saveMessages([]);
		},

		updateMessage(timestamp: number, updates: Partial<ChatMessage>) {
			update((state) => {
				const newMessages = state.messages.map((msg) =>
					msg.timestamp === timestamp ? { ...msg, ...updates } : msg,
				);
				saveMessages(newMessages);
				return { ...state, messages: newMessages };
			});
		},

		removeLastMessages(count: number) {
			update((state) => {
				const newMessages = state.messages.slice(0, -count);
				saveMessages(newMessages);
				return { ...state, messages: newMessages };
			});
		},

		removeHealthCheckErrors() {
			update((state) => {
				const newMessages = state.messages.filter((msg) => !msg.isHealthCheckError);
				saveMessages(newMessages);
				return { ...state, messages: newMessages };
			});
		},

		getConversationHistory(): Array<{ role: 'user' | 'assistant'; content: string }> {
			const state = get(aiSceneChatStore);
			return state.messages.map((msg) => ({
				role: msg.role,
				content: msg.role === 'user' ? msg.content.replace(/[#@$%](?=\S)/g, '') : msg.content,
			}));
		},

		consumeNewSessionFlag(): boolean {
			const state = get(aiSceneChatStore);
			if (state.needsNewSession) {
				update((s) => ({ ...s, needsNewSession: false }));
				return true;
			}
			return false;
		},

		reset() {
			set(getInitialState());
		},

		clearAllData() {
			localStorage.removeItem(STORAGE_KEY);
			LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));

			set({
				agentHealth: {},
				messages: [],
				isLoading: false,
				isModalOpen: false,
				showOnboardingModal: false,
				showPrivacyPolicyModal: false,
				privacyPolicyOpenedFromChat: false,
				hasAcceptedOnboarding: false,
				dismissedWithoutAccepting: false,
				unreadCount: 0,
				error: null,
				histories: [],
				currentHistoryId: null,
				activeAgentId: DEFAULT_AGENT_ID,
				agentUsage: buildDefaultUsage(),
				needsNewSession: true,
				chatPopoversCompleted: false,
				chatPopoverStep: 0,
				chatModalOpenTransitionDone: false,
			});
		},

		async saveCurrentToHistory() {
			const state = get(aiSceneChatStore);

			// Filter out health check error messages - they shouldn't be saved to history
			const persistableMessages = state.messages.filter((m) => !m.isHealthCheckError);

			if (persistableMessages.length === 0) return;

			const history: ChatHistory = {
				id: state.currentHistoryId || generateUUID(),
				title: generateTitle(persistableMessages),
				messages: persistableMessages,
				createdAt: persistableMessages[0]?.timestamp || Date.now(),
				updatedAt: Date.now(),
				agentId: state.activeAgentId,
			};

			const histories = loadAic().h;
			const existingIndex = histories.findIndex((h) => h.id === history.id);

			if (existingIndex !== -1) {
				histories[existingIndex] = history;
			} else {
				histories.unshift(history);
			}

			const trimmed = histories.slice(0, MAX_HISTORIES);
			saveHistories(trimmed);

			update((s) => ({ ...s, histories: trimmed }));

			aiSceneChatStore.syncChatPreferences();
		},

		loadHistory(historyId: string) {
			aiSceneChatStore.saveCurrentToHistory();

			const histories = loadAic().h;
			const history = histories.find((h) => h.id === historyId);

			if (!history) return;

			update((state) => ({
				...state,
				messages: history.messages,
				currentHistoryId: history.id,
			}));

			saveMessages(history.messages);
		},

		deleteHistory(historyId: string) {
			const histories = loadAic().h.filter((h) => h.id !== historyId);
			saveHistories(histories);
			update((state) => ({ ...state, histories }));
			aiSceneChatStore.syncChatPreferences();
		},

		refreshHistories() {
			const histories = loadAic().h;
			update((state) => ({ ...state, histories }));
		},

		setActiveAgent(agentId: AgentId) {
			update((state) => {
				const newState = { ...state, activeAgentId: agentId };
				savePrefs({
					a: agentId,
					u: compressUsage(newState.agentUsage),
				});
				return newState;
			});
			aiSceneChatStore.syncChatPreferences();
		},

		incrementPromptCount(agentId: AgentId) {
			const today = getTodayUTC();
			const now = new Date().toISOString();

			update((state) => {
				const prev = state.agentUsage[agentId];
				const isNewDay = prev.promptCountStartedAt !== today;

				const updated: AgentUsageStats = {
					dailyPromptCount: isNewDay ? 1 : prev.dailyPromptCount + 1,
					promptCountStartedAt: today,
					lastPromptAt: now,
				};

				const newUsage = { ...state.agentUsage, [agentId]: updated };

				savePrefs({
					a: state.activeAgentId,
					u: compressUsage(newUsage),
				});

				return { ...state, agentUsage: newUsage };
			});

			aiSceneChatStore.syncChatPreferences();
		},

		async syncChatPreferences() {
			const state = get(aiSceneChatStore);
			try {
				await postReq('/interface/ai/save', {
					id: 'chat_preferences',
					data: JSON.stringify({
						agent: state.activeAgentId,
						usage: state.agentUsage,
						messages: state.messages,
						histories: state.histories,
					}),
				});
			} catch (e) {
				console.warn('Failed to sync chat preferences to cloud:', e);
			}
		},

		async loadChatPreferences() {
			try {
				if (isSubUnit()) {
					return;
				}

				const res = await postReq('/interface/ai/list', { id: 'chat_preferences' });

				if (!res.isok || !res.data) return;

				let raw: any = res.data;
				if (raw?.ai?.chat_preferences) {
					raw = raw.ai.chat_preferences;
				}
				if (raw?.value && typeof raw.value === 'object') {
					raw = raw.value;
				}
				if (typeof raw === 'string') {
					raw = JSON.parse(raw);
				}

				const cloudAgentId: AgentId | undefined = raw?.agent;
				const cloudUsage = raw?.usage as Record<AgentId, AgentUsageStats> | undefined;
				const cloudMessages: ChatMessage[] = Array.isArray(raw?.messages) ? raw.messages : [];
				const cloudHistories: ChatHistory[] = Array.isArray(raw?.histories) ? raw.histories : [];

				update((state) => {
					const validAgentId =
						cloudAgentId && AGENTS.some((a) => a.id === cloudAgentId)
							? cloudAgentId
							: state.activeAgentId;

					const mergedUsage = cloudUsage
						? { ...buildDefaultUsage(), ...cloudUsage }
						: state.agentUsage;

					const newMessages = cloudMessages.length ? cloudMessages : state.messages;
					const newHistories = cloudHistories.length ? cloudHistories : state.histories;

					saveAic({
						m: newMessages,
						h: newHistories,
						p: { a: validAgentId, u: compressUsage(mergedUsage) },
					});

					return {
						...state,
						activeAgentId: validAgentId,
						agentUsage: mergedUsage,
						messages: newMessages,
						histories: newHistories,
					};
				});
			} catch (e) {
				console.warn('Failed to load chat preferences from cloud:', e);
			}
		},

		advanceChatPopoverStep() {
			const state = get(aiSceneChatStore);
			if (state.chatPopoverStep >= 3) {
				save_home(AI_CHAT_POPOVERS_KEY, { completed: true });
				update((s) => ({ ...s, chatPopoversCompleted: true }));
			} else {
				const nextStep = state.chatPopoverStep + 1;
				save_home(AI_CHAT_POPOVERS_KEY, { completed: false, step: nextStep });
				update((s) => ({ ...s, chatPopoverStep: nextStep }));
			}
		},

		skipChatPopovers() {
			save_home(AI_CHAT_POPOVERS_KEY, { completed: true });
			update((s) => ({ ...s, chatPopoversCompleted: true }));
		},

		resetChatPopovers() {
			save_home(AI_CHAT_POPOVERS_KEY, { completed: false, step: 0 });
			update((s) => ({ ...s, chatPopoversCompleted: false, chatPopoverStep: 0 }));
			toast.open({
				title: 'AI Chat Popovers Reset',
				type: 'success',
			});
		},
	};

	home_data.subscribe((hd) => {
		if (hd && typeof hd === 'object') {
			// Sync onboarding accepted state from home_data
			if (hasAcceptedOnboardingFromHomeData()) {
				update((state) => {
					if (state.hasAcceptedOnboarding || state.dismissedWithoutAccepting) return state;
					saveHasAcceptedOnboarding(true);
					return { ...state, hasAcceptedOnboarding: true, showOnboardingModal: false };
				});
			}

			// Sync popover state from home_data
			const popoversState = getChatPopoversStateFromHomeData();
			update((state) => {
				if (
					state.chatPopoversCompleted === popoversState.completed &&
					state.chatPopoverStep === popoversState.step
				) {
					return state;
				}
				// Do not overwrite with stale home_data when user just advanced in this session
				if (state.chatPopoverStep > popoversState.step) {
					return state;
				}
				return {
					...state,
					chatPopoversCompleted: popoversState.completed,
					chatPopoverStep: popoversState.step,
				};
			});
		}
	});

	return aiSceneChatStore;
}

export const aiSceneChatStore = createAISceneChatStore();
